const GROUP_FILTER_KEY = 'filter-group';
const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const FIELD_ID_PATTERN = /^[a-zA-Z0-9_][a-zA-Z0-9_-]*(?:\.[a-zA-Z0-9_][a-zA-Z0-9_-]*)*$/;
const RESERVED_FIELD_IDS = new Set(['page', 'sort', 'keyword-search', 'filter-group', 'discount']);
const UNSAFE_FIELD_SEGMENTS = new Set(['__proto__', 'prototype', 'constructor']);

const isSafeFieldId = (fieldId) => typeof fieldId === 'string'
    && FIELD_ID_PATTERN.test(fieldId)
    && fieldId.length <= 200
    && !fieldId.toLowerCase().includes('keyword-search')
    && !fieldId.toLowerCase().includes('discount')
    && !fieldId.toLowerCase().includes('-min')
    && !fieldId.toLowerCase().includes('-max')
    && !fieldId.split('.').some((part) => UNSAFE_FIELD_SEGMENTS.has(part.toLowerCase()) || RESERVED_FIELD_IDS.has(part.toLowerCase()));

const asGroupIds = (value) => {
    const values = Array.isArray(value) ? value : [value];
    return values.filter((id) => typeof id === 'string' && id !== '');
};

const sameId = (left, right) => String(left).toLowerCase() === String(right).toLowerCase();

const getExplicitFilterGroupValues = (values) => {
    const uniqueValues = [];
    if (Array.isArray(values)) {
        values.forEach((value) => {
            if (typeof value === 'string' && !uniqueValues.includes(value)) uniqueValues.push(value);
        });
    }
    return uniqueValues;
};

const getFilterGroupValues = (group) => {
    const values = getExplicitFilterGroupValues(group?.values);

    const label = typeof group?.label === 'string' ? group.label.trim() : '';
    if (label && !values.includes(label)) values.push(label);
    return values;
};

const getFilterGroups = (store) => {
    const config = store?.filterGroups;
    if (!config || config.version !== 1 || !Array.isArray(config.groups)) return [];

    const seenIds = new Set();
    const groups = config.groups.reduce((validGroups, group) => {
        const id = typeof group?.id === 'string' ? group.id.trim().toLowerCase() : '';
        const fieldId = group?.fieldId;
        const label = typeof group?.label === 'string' ? group.label.trim() : '';
        if (!UUID_PATTERN.test(id) || !isSafeFieldId(fieldId) || !label
            || !Array.isArray(group?.values) || seenIds.has(id)) return validGroups;

        seenIds.add(id);
        const values = getExplicitFilterGroupValues(group.values);
        validGroups.push({
            id,
            fieldId,
            label,
            values,
            allowExpansion: typeof group.allowExpansion === 'boolean'
                ? group.allowExpansion
                : config.allowExpansion === true,
        });
        return validGroups;
    }, []);

    // Legacy configurations may already assign a same-name value elsewhere.
    // Explicit membership wins; new conflicts are rejected by settings validation.
    const explicitOwnersByField = new Map();
    groups.forEach((group, groupIndex) => {
        let ownersByValue = explicitOwnersByField.get(group.fieldId);
        if (!ownersByValue) {
            ownersByValue = new Map();
            explicitOwnersByField.set(group.fieldId, ownersByValue);
        }
        group.values.forEach((value) => {
            let owners = ownersByValue.get(value);
            if (!owners) {
                owners = new Set();
                ownersByValue.set(value, owners);
            }
            owners.add(groupIndex);
        });
    });

    const implicitOwnersByField = new Map();
    return groups.map((group, groupIndex) => {
        const values = getFilterGroupValues(group);
        const labelIsExplicit = group.values.includes(group.label);
        if (labelIsExplicit) return {...group, values};

        const explicitOwners = explicitOwnersByField.get(group.fieldId)?.get(group.label);
        if (explicitOwners && [...explicitOwners].some((ownerIndex) => ownerIndex !== groupIndex)) {
            return {...group, values: group.values.slice()};
        }

        let implicitOwners = implicitOwnersByField.get(group.fieldId);
        if (!implicitOwners) {
            implicitOwners = new Set();
            implicitOwnersByField.set(group.fieldId, implicitOwners);
        }
        if (implicitOwners.has(group.label)) return {...group, values: group.values.slice()};

        implicitOwners.add(group.label);
        return {...group, values};
    });
};

const getGroupById = (id, store) => {
    if (typeof id !== 'string') return null;
    return getFilterGroups(store).find((group) => sameId(group.id, id)) || null;
};

const resolveGroupFilters = (activeFilters, store) => {
    const active = activeFilters && typeof activeFilters === 'object' && !Array.isArray(activeFilters)
        ? activeFilters
        : {};
    const resolved = {...active};
    const selectedGroups = asGroupIds(active[GROUP_FILTER_KEY]);
    delete resolved[GROUP_FILTER_KEY];

    const groupsById = new Map(getFilterGroups(store).map((group) => [group.id, group]));
    const emptyGroupFields = new Set();

    selectedGroups.forEach((id) => {
        const group = groupsById.get(id.toLowerCase());
        if (!group) return;

        const existing = resolved[group.fieldId];
        const values = Array.isArray(existing)
            ? existing.filter((value, index) => existing.indexOf(value) === index)
            : existing === undefined || existing === null || existing === ''
                ? []
                : [existing];
        group.values.forEach((value) => {
            if (!values.includes(value)) values.push(value);
        });
        resolved[group.fieldId] = values;
        if (group.values.length === 0) emptyGroupFields.add(group.fieldId);
    });

    // Preserve an explicitly selected empty group as a field with no values. The
    // query builder turns that into match_none instead of dropping the filter.
    emptyGroupFields.forEach((fieldId) => {
        if (!Array.isArray(resolved[fieldId]) || resolved[fieldId].length === 0) {
            resolved[fieldId] = [];
        }
    });

    return resolved;
};

const getGroupSelection = (group, activeFilters, store) => {
    const configuredGroup = getGroupById(group?.id, store);
    if (!configuredGroup) return {checked: false, indeterminate: false, selectedCount: 0};

    const resolved = resolveGroupFilters(activeFilters, store);
    const rawValues = resolved[configuredGroup.fieldId];
    const selectedValues = new Set(Array.isArray(rawValues)
        ? rawValues
        : rawValues === undefined || rawValues === null || rawValues === '' ? [] : [rawValues]);
    const selectedCount = configuredGroup.values.filter((value) => selectedValues.has(value)).length;
    const groupIsSelected = asGroupIds(activeFilters?.[GROUP_FILTER_KEY])
        .some((id) => sameId(id, configuredGroup.id));
    const checked = groupIsSelected || (configuredGroup.values.length > 0
        && selectedCount === configuredGroup.values.length);

    return {
        checked,
        indeterminate: !checked && selectedCount > 0,
        selectedCount,
    };
};

const removeGroupValues = (activeFilters, group) => {
    const active = {...activeFilters};
    const current = active[group.fieldId];
    if (Array.isArray(current)) {
        const remaining = current.filter((value) => !group.values.includes(value));
        if (remaining.length) active[group.fieldId] = remaining;
        else delete active[group.fieldId];
    } else if (group.values.includes(current)) {
        delete active[group.fieldId];
    }
    return active;
};

const setGroupIds = (active, ids) => {
    const uniqueIds = ids.filter((id, index) => ids.findIndex((candidate) => sameId(candidate, id)) === index);
    if (!uniqueIds.length) delete active[GROUP_FILTER_KEY];
    else active[GROUP_FILTER_KEY] = uniqueIds.length === 1 ? uniqueIds[0] : uniqueIds;
    return active;
};

const toggleGroupSelection = (group, activeFilters, store) => {
    const configuredGroup = getGroupById(group?.id, store);
    if (!configuredGroup) return {...(activeFilters || {})};

    const active = activeFilters && typeof activeFilters === 'object' && !Array.isArray(activeFilters)
        ? {...activeFilters}
        : {};
    const existingIds = asGroupIds(active[GROUP_FILTER_KEY]);
    const isChecked = getGroupSelection(configuredGroup, active, store).checked;
    const next = removeGroupValues(active, configuredGroup);

    if (isChecked) {
        return setGroupIds(next, existingIds.filter((id) => !sameId(id, configuredGroup.id)));
    }

    return setGroupIds(next, [...existingIds, configuredGroup.id]);
};

module.exports = {
    GROUP_FILTER_KEY,
    isSafeFieldId,
    getFilterGroupValues,
    getFilterGroups,
    resolveGroupFilters,
    getGroupSelection,
    toggleGroupSelection,
};
